﻿// © Microsoft Corporation. All rights reserved.

// Implements multiple parallel tracking engine which gets its tracking
// url from the specific implementation using defined functions.
//
// if called multiple times during page interaction (representing significant content
// update), the trackPage method will also publish a "pageView" mediator event that third-party
// tracking code can hook to register their refresh page-view beacons.

/// <summary>
///    This function implements multiple parallel tracking engine which gets its tracking
///    url from the specific implementation using defined functions.
/// </summary>
/// <param name="trackInfo" type="object">The tracking info JS library</param>
/// <param name="extend" type="object">The JS library that provides functionality similar to jQuery's extend function</param>
/// <param name="dom" type="object">The dom manipulation JS library</param>
/// <param name="getCookie" type="object">The JS library for getting cookies</param>
/// <param name="setCookie" type="object">The JS library for setting cookies</param>
/// <param name="events" type="object">The events object</param>
/// <param name="window" type="object">The window object</param>
/// <param name="document" type="object">The document object.</param>
/// <param name="mediator" type="object">The mediator JS library</param>
/// <param name="navigation" type="object">Provides access to current navigation URL and helpers</param>
/// <param name="headData" type="object">Collection of data passed from the server</param>
/// <param name="pageInstance" type="object">The page instance object</param>
/// <param name="evaluate" type="object">Cross browser support for json parse</param>
/// <param name="trackingHelper" type="object">The tracking helper object</param>
/// <param name="storage" type="object">The storage object</param>
/// <param name="perfMarker" type="object">The performance marker object</param>
/// <param name="telemetryConstants type="object">The telemetry constant object</param>
/// <param name="requestPageRevealAsync" type="object">The JS library that provides functionality for callback when page is revealed. Used for preload scenarios.</param>
/// <param name="telemetryDataTypes" type="object">The datatypes for telemetry library</param>
/// <param name="webStorage" type="object">The webStorage object</param>
/// <return>The track object</return>

import { Severity, logger } from "@msnews/diagnostics";
import { canUseDOM, getLocationHost, getLocationHref, getLocationProtocol } from "@msnews/isomorphic-util";

import dom from "./dom";
import events from "./events";
import extend from "./extend";
import mediator from "./mediator";
import navigation from "./navigation";
import pageInstance from "./pageInstance";
import { sessionStorage } from "@msnews/core";
import telemetryConstants from "./telemetryconstants";
import { telemetryDataTypes } from "./telemetryDataTypes";
import { telemetryReadyCallback } from "./telemetryReadyCallback";
import { trackInfo } from "./trackInfo";
import trackingHelper from "./trackingHelper";

// shortcuts to dom functions
var attr = dom.attr;
var domName = dom.name;
var getParent = dom.parent;
var getChildren = dom.children;
var recipients = [];
var refreshPageLoad;
var doc = canUseDOM() ? document : null;
var errorLogInterval = 5 * 60 * 1000;

var lastClickedTarget = null;

// After 500 ms, lastClickedTarget will be set as null.
var clickTargetResetTimeoutInMs = 500;

// Token to find the custom event token passed on the url. the format would be like this - http://www.msn.com/#tevt=click_nonnav;tobjid=....;...
var tEvtTag = 'tevt=';

// If there are more than one tags(name value pair), then the name value pair should be separated with ;
var regExpEventName = /#tevt=([A-Za-z0-9]+_[A-Za-z0-9]+)(;*)/g;

// an array of ids that represent what we've already sent to the service as updates
// TODO (rohjain): Remove this cache as we don't need two caches one in trackInfo and another here to maintain for impression updates cache.
var impressionCache = [];

// error report interval in milliseconds (5 min =  5 * 60 * 1000)
var errorReportInterval = telemetryConstants.logSamplingRate || 5 * 60 * 1000;

mediator.sub("logerror", logErrorEvent);

function trackAll(implementationMethod) {
    ///<summary> After createReport returns a report, this method fires tracking call for each registered tracking system</summary>

    var implementation;

    var parameters = canUseDOM() ? navigation.getParamsFromUrl(getLocationHref()) : {};

    // loop through each implementation
    for (var ndx = 0; ndx < recipients.length; ndx++) {
        implementation = recipients[ndx];
        var defOpts = implementation.defaultOpts;
        if (defOpts.disableOnAutoRefresh) {
            var names = defOpts.disableOnAutoRefresh.split(';');
            var isInList = false;
            for (var i = 0; i < names.length; i++) {
                if (names[i] == defOpts.id) {
                    isInList = true;
                    break;
                }
            }

            // @ts-ignore
            if (isInList && parameters.AR) {
                // In case this was generated early, reset to false so the next pageTrack() call will generate the beacon.
                defOpts.isGeneratedEarly = false;
                continue;
            }
        }

        if (defOpts.isGeneratedEarly) {
            // Don't generate this time, but reset to false so the next pageTrack() call will generate the beacon.
            defOpts.isGeneratedEarly = false;
        } else if (implementation) {
            // TODO: (nubenja) Remove samplingRate everywhere (implementation.samplingRate)
            var result = implementation[implementationMethod]();
        }
    }

}

function logErrorEvent(errorObject) {
    // /// <summary>Track error event</summary>
    // ///<param name="errorObject" type="Object" optional="true">
    // ///     errorObject- defined in errorCategory
    // ///</param>
    // if (!errorObject)
    // {
    //     return;
    // }
    // var cacheKey = errorObject.errId + errorObject.errSource;

    // var lastErrorTime = storage.getItem(cacheKey, true);
    // var currentTime = new Date().getTime();
    // if (lastErrorTime && (currentTime - lastErrorTime) <= errorLogInterval)
    // {
    //     return;
    // }

    // trackInfo.event = {
    //     type: "app_error"
    // };
    // trackInfo.report = errorObject;
    // logDiag(JSON.stringify(errorObject), true);

    // trackAll("getEventTrackingUrl");
    // storage.setItem(cacheKey, currentTime, true);
}

function depthFirstChildAttribute(element, attributeName, skipElement?) {
    /// <summary>
    ///     find the first attribute from the element in the depthFirst manner
    ///     skips the element if it is specified in elementName
    /// </summary>
    var children = getChildren(element) || [];
    var ndx = 0;
    var altValue;

    attributeName = attributeName || "alt";

    for (; ndx < children.length; ndx++) {
        altValue = attr(children[ndx], attributeName)
            || depthFirstChildAttribute(children[ndx], attributeName, skipElement);

        if (altValue && !(skipElement == children[ndx].localName)) {
            return altValue;
        }
    }
}

function findCMValue(element) {
    /// <summary>
    ///     create a list of id values separated with '>'
    ///     walking from generic to specific
    ///     ending at (not including) wrapper on the generic side
    /// </summary>
    if (element) {
        var parent = getParent(element);
        var curId = attr(parent, "id");
        var previousCM;
        if (trackInfo.wrapperId == curId) {
            return;
        }
        previousCM = findCMValue(parent);
        if (previousCM && curId) {
            return [previousCM, curId].join(trackInfo.cmSeparator);
        }
        return curId || previousCM;
    }
}

function getContentElementIndex(element) {
    /// <summary>
    ///     count number of trackable elements upto and including the
    ///     element passed into the function
    ///     stop at the first parent with an id
    ///     do not use anything with a new id
    ///     indexing all elements with the same CM value
    ///     indexing starts at 1
    ///     0 means there is no parent with an id
    /// </summary>

    if (!element) {
        return;
    }

    var parent = getParent(element);
    var children;
    var count = 0;
    var ndx = 0;

    // go up first, incase there is no parent id so we don't have to waste a lot of work
    if (!attr(parent, "id")) {
        count = getContentElementIndex(parent);
        if (count) {
            // by default getContentElementIndex will count the element passed in
            // subtract 1 to offset this
            count--;
        } else {
            // only stays 0 if there is no parent or no parent with an id
            // bubble up the expected 0 value
            return 0;
        }
    }

    children = getChildren(parent) || [];
    for (; ndx < children.length; ndx++) {
        if (children[ndx] == element) {
            count++;
            break;
        }
        count += countAllTrackableLinks(children[ndx]);
    }

    return count;
}

function countAllTrackableLinks(element) {
    /// <summary>
    ///     returns count of all trackable elements[contains href and notrack]
    /// </summary>
    var count = 0;
    var ndx = 0;
    var children;
    if (element && !attr(element, "id")) {
        children = getChildren(element) || [];
        if (attr(element, "href") && !attr(element, trackInfo.notrack)) {
            count++;
        }
        for (; ndx < children.length; ndx++) {
            count += countAllTrackableLinks(children[ndx]);
        }
    }
    return count;
}

function getChildByTargetElement(element, tagName, depth) {
    /// <summary>
    /// Finds the first child element within the provided element with the desired tagName.
    /// Goes through all the children/grandchildren to the provided depth (1 = children, 2 = first grandchildren)
    /// </summary>
    /// <returns>Element with tagName if found, null if not</returns>
    if (depth && element && tagName) {
        if (element.tagName == tagName) {
            // They passed in an element with the correct tagName value.
            return element;
        }

        var children = getChildren(element) || [];
        for (var ndx = 0, dataLength = children.length; ndx < dataLength; ndx++) {
            if (children[ndx].tagName == tagName) {
                return children[ndx];
            } else {
                var subChild = getChildByTargetElement(children[ndx], tagName, depth - 1);
                if (subChild) {
                    return subChild;
                }
            }
        }
    }
    return null;
}

function sendSinglePageViewBeacon(beaconId) {
    /// <summary>
    /// This method generates a single beacon without firing any other beacons.
    /// Do NOT call track.trackPage() if this is used, or you will generate two of these beacons.
    /// </summary>
    /// <param name="beaconId" type="string" optional="false">
    ///     The single beacon to generate.
    /// </param>

    if (beaconId) {
        // loop through each implementation
        var implementation;
        var implementationMethod = "getPageViewTrackingUrl";
        var ndx = 0;

        for (; ndx < recipients.length; ndx++) {
            implementation = recipients[ndx];
            if (implementation.defaultOpts.id === beaconId) {
                var result = implementation[implementationMethod]();
                break;
            }
        }
    }
}

function sendPageViewCandidate() {
    /// <summary>
    /// This fires a request for a page view candidate file required by ComScore.
    /// Call this directly, if you are generating new page views which include ComScore and
    /// need to identify it as a page view candidate.
    /// </summary>

    var pvUrl = getLocationProtocol() + "//" + getLocationHost(); + "/pv.xml";
    if (!trackInfo.shouldFireTelemetry()) {
        return;
    }

    var beaconData = {
        url: pvUrl,
        requestType: telemetryConstants.GET,
        trackingType: telemetryConstants.Comscore
    };

    mediator.pub(telemetryConstants.FireTelemetry, beaconData);
}

function trackAdEvent(adParams) {
    ///<summary>
    ///     This method tracks the adimpr_update event.
    ///     Call getEventTrackingUrl to fire the tracking call.
    ///</summary>
    ///<param name="adParams" type="Object" optional="true">
    ///     adParams- defined as follows
    ///     adParams = {
    ///             dst: dapStartTime,
    ///             den: dapEndTime,
    ///             id: placementId,
    ///             pg: pageGroup
    ///             w: width,
    ///             h: height,
    ///             status: status,
    ///             anAd: appNexusId
    ///             seqid: sequenceId
    ///             sdk: sdk
    ///             fen: finalEndTime
    ///     };
    ///</param>

    trackInfo.event = { type: "adimpr_update" };
    var adWidth = parseInt(adParams.w);
    var adHeight = parseInt(adParams.h);

    // @ts-ignore
    trackInfo.report =
    {
        ///<summary>
        ///     This creates a tracking report.
        ///     It does NOT send the report, nor does it perform any navigation.
        ///</summary>
        ///<returns type="object">
        ///     Report object that is structured as follows:
        ///     report = {
        ///                dst: dapStartTime,
        ///                den: dapEndTime,
        ///                id: placementId,
        ///                pg: pageGroup
        ///                w: width,
        ///                h: height,
        ///                status: status,
        ///                anAd: appNexusId
        ///                seqid: sequenceId
        ///                sdk: sdk
        ///                fen: finalEndTime
        ///             };
        /// </returns>

        dst: adParams.dst,
        den: adParams.den,
        id: adParams.id,
        pg: adParams.pg,
        w: isNaN(adWidth) ? 0 : adWidth,
        h: isNaN(adHeight) ? 0 : adHeight,
        status: adParams.status,
        anAd: adParams.anAd,
        seqid: adParams.seqid,
        sdk: adParams.sdk,
        fen: adParams.fen
    };

    trackAll("getEventTrackingUrl");
}

function trackAppErrorEventInternal(errorParams) {
    ///<summary>
    ///     This method tracks the app_error event.
    ///     Call getEventTrackingUrl to fire
    ///     the tracking call.
    ///</summary>
    ///<param name="errorParams" type="Object" optional="true">
    ///     errorParams- defined in errorCategory
    ///</param>

    if (!errorParams) {
        return;
    }

    // Check if it should fire app error.
    if (!trackInfo.shouldFireAppError()) {
        return;
    }

    if (!errorParams.severity) {
        errorParams.severity = Severity.Alert;
    }

    setAppErrorTrackInfo(errorParams);
    trackAll("getEventTrackingUrl");
}

function setAppErrorTrackInfo(errorParams) {
    ///<summary>Sets the track information object</summary>

    var errEventObj = { type: "app_error" };
    trackInfo.event = errEventObj;

    // @ts-ignore
    var report = trackInfo.report =
    {
        ///<summary>
        ///     This creates a tracking report.
        ///     It does NOT send the report, nor does it perform any navigation.
        ///
        ///</summary>
        ///<param name="errId" type="string" optional="true">
        ///     Error Id - defined in errorCategory
        ///</param>
        ///<param name="errMsg" type="string" optional="true">
        ///     Error Message - defined in errorCategory
        ///</param>
        ///<param name="errSource" type="string" optional="true">
        ///     Error Source - defined in errorCategory
        ///</param>
        //<param name="severity" type="Boolean" optional="true">
        ///     severity - defined in AppErrorTracker.ts
        ///</param>
        ///<param name="pb" type="object" optional="true">
        ///     Pb (property bag) - defined in AppErrorTracker.ts
        ///</param>
        ///<returns type="object">
        ///     Report object that is structured as follows:
        ///     report = {
        ///             errId: ErrorId,
        ///             errMsg: ErrorMsg,
        ///             errMsg: ErrorSource,
        ///             severity: severity,
        ///             pb: pb
        ///     };
        /// </returns>
        errId: errorParams.id,
        errMsg: errorParams.message,
        errSource: errorParams.source,
        severity: errorParams.severity,
        pb: errorParams.pb
    };

    logDiag(JSON.stringify(report), true);
}

function trackFormSubmitEvent(event, element, destination, headline, module, index, campaign) {
    /// <summary>
    ///  This method tracks form events.
    ///     This methods try to gets the search term.
    ///     Call trackEvent
    /// </summary>
    /// <param name="event" type="object" optional="true">
    ///     Event object
    /// </param>
    /// <param name="element" type="object" optional="true">
    ///     element that was clicked or target of the event. Passed to createReport.
    /// </param>
    /// <param name="destination" type="string" optional="true">
    ///     destination url to use instead of computing. Passed to createReport.
    /// </param>
    /// <param name="headline" type="string" optional="true">
    ///     headline to use instead of computing. Passed to createReport.
    ///	</param>
    ///	<param name="module" type="string" optional="true">
    ///     content module string to be used instead of computing. Passed to createReport.
    /// </param>
    /// <param name="index" type="string" optional="true">
    ///     content index string to be used instead of computing. Passed to createReport.
    /// </param>
    /// <param name="campaign" type="string" optional="true">
    ///     campaign id to be used instead of computing. Passed to createReport.
    /// </param>

    ///Helper functions.
    function isSearchForm() {
        var searchFormId = "srchfrm";
        if (!element && event) {
            element = event.target;
        }

        // if element not does not exist or is non trackable
        if (!element || attr(element, trackInfo.notrack)) {
            return false;
        }
        return element.id === searchFormId;
    }

    function getSearchTerm() {
        var searchBox = document.getElementById("q");
        if (searchBox) {
            // @ts-ignore
            return searchBox.value;
        }
    }
    //End of helper functions

    if (isSearchForm()) {
        headline = getSearchTerm();
    }

    trackEvent(event, element, destination, headline, module, index, campaign);
}

function trackEvent(event, element?, destination?, headline?, module?, index?, campaign?, contentAction?) {
    /// <summary>
    ///     This method tracks events.
    ///     This methods loops through each implementation,
    ///     Call getEventTrackingUrl and call that url to fire
    ///     the tracking call.
    /// </summary>
    /// <param name="event" type="object" optional="true">
    ///     Event object
    /// </param>
    /// <param name="element" type="object" optional="true">
    ///     element that was clicked or target of the event. Passed to createReport.
    /// </param>
    /// <param name="destination" type="string" optional="true">
    ///     destination url to use instead of computing. Passed to createReport.
    /// </param>
    /// <param name="headline" type="string" optional="true">
    ///     headline to use instead of computing. Passed to createReport.
    /// </param>
    /// <param name="module" type="string" optional="true">
    ///     content module string to be used instead of computing. Passed to createReport.
    /// </param>
    /// <param name="index" type="string" optional="true">
    ///     content index string to be used instead of computing. Passed to createReport.
    /// </param>
    /// <param name="campaign" type="string" optional="true">
    ///     campaign id to be used instead of computing. Passed to createReport.
    /// </param
    /// <param name="contentAction" type="integer" optional="true">
    ///     contentAction enum of the event. Please see list of enum values under telemetryDatatypes.js
    /// </param>

    // if element is not passed in, try to get it from event
    if (!element && event) {
        element = event.target;
    }

    // if element not does not exist or is non trackable
    if (!element || attr(element, trackInfo.notrack)) {
        return;
    }

    // for bug 1114449
    // element is a jQuery object, not an element
    // break out element from jQuery wrapper
    if (element.jquery) {
        element = element[0];
    }

    // Make sure that the trackInfo event does not have a jQuery object for target, or AOP will fail to find a parent.
    event = event || {};
    // Mark the event that it is already custom handled.
    event.customHandled = true;

    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Errors/Read-only
    // event.target = element;
    trackInfo.event = event;
    var elemHref = element.href || attr(element, "href");

    // Temp. Fix: element.getAttribute(attr, 2) returns # only for non-navigational url. Need to get destination with full domain path.
    if (elemHref == "#") {
        elemHref = element["href"];
    }

    // find destination value if needed
    destination = destination || attr(element, trackInfo.piiurl) ||
        elemHref || attr(element, "action") || "";

    // TODO: clean up how we find a headline. Depending on the markup of the page leads to bugs when we update it.
    // find headline value if needed, one may have been passed in through a data-hl attribute.
    var dataHlValue = attr(element, "data-hl") || "";
    if (!headline && !dataHlValue) {
        // See if there is an h4 or h3 element, no more than 2 deep.
        var headlineElement = getChildByTargetElement(element, "H4", 2) || getChildByTargetElement(element, "H3", 2);
        if (headlineElement) {
            headline = dom.text(headlineElement);
        }
    }

    if (typeof headline !== "string") {
        headline = dataHlValue || attr(element, trackInfo.piitxt) ||
            ("FORM" == domName(element)
                ? trackInfo.defaultFormHeadline
                : attr(element, "title") || depthFirstChildAttribute(element, "title", "img") || dom.text(element) || attr(element, "alt") || depthFirstChildAttribute(element, "alt") || "");
    }

    // Added condition to call trim function only if it is defined, it fails for browsers <=IE8
    if (headline.trim != undefined) {
        headline = headline.trim();
    }

    // find module value if needed
    module = module || findCMValue(element) || trackInfo.defaultModule;

    // find index value if needed
    index = index || (attr(element, "id") ? 1 : getContentElementIndex(element));

    // find campaign value if needed
    // Added backward compatibility for Old GT1 implementation. If "GT1-xxxx" not defined in class, look it into href attribute.
    // Example when GT1 defined in a class: class="GT1-12345"
    // Example when GT1 is a part of href attribute: href="www.msn.com?GT1=12345"
    var clsGT1 = element.className || attr(element, "class");
    campaign = campaign || (/GT1-(\d+)\b/i.exec(clsGT1) ? RegExp.$1 : "") ||
        (/[?&]GT1=(\d+)\b/i.exec(elemHref) ? RegExp.$1 : "");

    var moduleInfo = "";
    var nameLineage = '';
    var ordinalLineage = '';
    var lineageDelimiter = trackInfo.cmSeparator;
    var eventTime;

    if (event && (event.type == trackInfo.eventTypes.click || event.type == trackInfo.eventTypes.nonNavClckEvt || event.type == trackInfo.eventTypes.submit || event.type == trackInfo.eventTypes.contextmenu || event.type == trackInfo.eventTypes.mouseenter)) {
        var moduleInfoHeader = "data-m";
        moduleInfo = attr(element, moduleInfoHeader);
        if (!moduleInfo) {
            var parent = getParent(element);
            moduleInfo = parent ? attr(parent, moduleInfoHeader) : "";
        }

        try {
            // TODO: (nubenja) Remove this once data-m.y is deprecated
            // Convertion from telemetryType (e1.y) to telemetryContentType (e1.ct) and telemetryContentAction (ca)
            var moduleInfoJson = JSON.parse(moduleInfo);
            if (moduleInfoJson && moduleInfoJson.y && !moduleInfoJson.ct) {
                var ct = telemetryDataTypes.getTelemetryContentType(moduleInfoJson.y);

                if (ct) {
                    moduleInfoJson.ct = ct;
                }
            }

            var actionType;
            var behaviorType;

            // Map behavior
            if (moduleInfoJson && moduleInfoJson.y) {
                behaviorType = telemetryDataTypes.getTelemetryBehavior(moduleInfoJson.y);
            }

            // Map action type
            if (event.originalEvent === trackInfo.eventTypes.contextmenu) {
                actionType = telemetryDataTypes.TelemetryActionType.RightClick;
                behaviorType = telemetryDataTypes.TelemetryBehaviorType.ContextMenu;
            } else if (event.type === trackInfo.eventTypes.click) {
                actionType = telemetryDataTypes.TelemetryActionType.Click;
                behaviorType = telemetryDataTypes.TelemetryBehaviorType.Navigate;
            } else if (event.type === trackInfo.eventTypes.nonNavClckEvt) {
                actionType = telemetryDataTypes.TelemetryActionType.Click;
            } else if (event.type === trackInfo.eventTypes.submit) {
                actionType = telemetryDataTypes.TelemetryActionType.Submit;
            }
        } catch (e) {
            moduleInfoJson = null;
        }

        var elementTelemetryLineage = getLineage(element);
        if (elementTelemetryLineage) {
            nameLineage = elementTelemetryLineage.nameLineage.reverse().join(lineageDelimiter);
            ordinalLineage = elementTelemetryLineage.ordinalLineage.reverse().join(lineageDelimiter);
        }

        // assign the time if event already has time field. (added from trackClickEvents)
        eventTime = event.time;
    }

    // check if destination url is bing or not. if it is, remove all telemetry sensitive data
    var duHostname = navigation.getHostName(destination);
    if (duHostname && duHostname.toLowerCase().indexOf("bing.com") > -1) {
        // filter out privacy sensitive params
        destination = track.filterUrlParams(destination, ["q", "pq"]);

        // remove headline
        headline = "";
    }

    // @ts-ignore
    trackInfo.report =
    {
        /// <summary>
        ///     This method creates a tracking report.
        ///     It does NOT send the report, nor does it perform any navigation.
        ///
        /// </summary>
        /// <param name="element" type="object" optional="true">
        ///     element that was clicked or target of the event.
        /// </param>
        /// <param name="destination" type="string" optional="true">
        ///     destination url to use instead of computing.
        /// </param>
        /// <param name="headline" type="string" optional="true">
        ///     headline to use instead of computing.
        /// </param>
        /// <param name="module" type="string" optional="true">
        ///     content module string to be used instead of computing.
        /// </param>
        /// <param name="index" type="string" optional="true">
        ///     content index string to be used instead of computing.
        /// </param>
        /// <param name="campaign" type="string" optional="true">
        ///     campaign id to be used instead of computing.
        /// </param>
        /// <param name="module" type="string" optional="true">
        ///     data-m section to be used instead of computing.
        /// </param>
        /// <returns type="object">
        ///     Report object that is structured as follows:
        ///     report = {
        ///             destinationUrl: destination,
        ///             campaignId: '',
        ///             contentElement: index,
        ///             contentModule: module,
        ///             headline: headline,
        ///             sourceIndex,
        ///             nodeName,
        ///             module: moduleInfo,
        ///             nLineage: nameLineage,
        ///             oLineage: ordinalLineage,
        ///             timeToInteract: trackInfo.timeToInteract()
        ///     };
        /// </returns>
        destinationUrl: destination,
        headline: headline,
        contentModule: module,
        contentElement: index,
        campaignId: campaign,
        module: moduleInfoJson,
        sourceIndex: element.sourceIndex || "",
        nodeName: element.nodeName || "",
        nLineage: nameLineage,
        oLineage: ordinalLineage,
        timeToInteract: trackInfo.timeToInteract(eventTime),
        actionType: actionType,
        behaviorType: behaviorType
    };

    trackAll("getEventTrackingUrl");
}

function trackPageInternal(overrideParams, persistOverriddenParams) {
    /// <summary>
    ///    Fires page view call for each registered tracking system at the top of <Body /> tag
    /// </summary>
    /// <param name="overrideParams" type="object">override client side params</param>
    /// <param name="persistOverriddenParams" type="boolean">if true overridden params are persisted for other events otherwise false. Default is false</param>

    var perfMarkerTrackingPageViewStart = "TimeToTrackingPageViewStart";
    var perfMarkerTrackingPageViewEnd = "TimeToTrackingPageViewEnd";

    // initialize params and assign default as false.
    persistOverriddenParams = !!persistOverriddenParams;

    // clear event in case trackPage is called more than once on a page
    delete trackInfo.event;

    // Clear the request id to generate the new request id
    // @ts-ignore
    delete trackInfo.userStatic.requestId;

    // Clear the override Client Params for every track page request and reassign the parameter.
    delete trackInfo.overrideClientParams;

    var paramPageName = "pg.n";
    var pageName = overrideParams && overrideParams[paramPageName];
    if (pageName) {
        var paramPageType = "pg.t";
        var telemetryPageName = telemetryDataTypes.getTelemetryPageInfo(pageName);
        if (telemetryPageName) {
            overrideParams[paramPageName] = telemetryPageName.PageName;

            var pageType = overrideParams[paramPageType];
            var telemetryPageType = telemetryDataTypes.validateTelemetryDataType(telemetryDataTypes.TelemetryPageType, pageType);
            if (telemetryPageType) {
                overrideParams[paramPageType] = telemetryPageType;
            } else if (telemetryPageName.PageType === telemetryDataTypes.TelemetryPageType.Unspecified) {
                // @ts-ignore
                overrideParams[paramPageType] = trackInfo.sitePage.page_type;
            } else {
                overrideParams[paramPageType] = telemetryPageName.PageType;
            }
        }
    }

    // This is needed since in prime we include this so that
    // telemetry team knows that the page should be ignored due to
    // automatic refresh
    var signInRefreshedPageKey = "sign-in-refreshed-page";
    if (sessionStorage.getItem(signInRefreshedPageKey)) {
        sessionStorage.removeItem(signInRefreshedPageKey);
        (overrideParams = overrideParams || {}).ar = 1;
    }

    trackInfo.overrideClientParams = overrideParams;
    trackInfo.persistOverriddenParams = persistOverriddenParams;

    var result = trackingHelper.modifyValuesForTrackPage();
    if (result && result.preventComscore) {
        track.preventBeacon(telemetryConstants.Comscore);
    }

    trackAll("getPageViewTrackingUrl");

    // the refreshPageLoad value is undefined (false) at first page load request, and
    // we then set it to one (true).
    // subsequent calls to trackPage will then publish the mediator pageView event
    // that code can listen for to know when the page is generating a "page view"
    // due to significant content change.
    if (refreshPageLoad) {
        mediator.pub("pageView");
    } else {
        refreshPageLoad = 1;
        mediator.pub("pageViewOnLoad");
    }

    // Clear the override Client Params once the trackPage is fired so that other events don't use the overridden params from memory.
    delete trackInfo.overrideClientParams;
}

export var track =
{
    onContextMenu: onContextMenu,
    onClick: onClick,

    trackEvent: trackEvent,
    trackFormSubmitEvent: trackFormSubmitEvent,
    trackAdEvent: trackAdEvent,
    trackVideoEvent: trackVideoEvent,
    trackFeedbackEvent: trackFeedbackEvent,

    createEvent: createEvt,
    sendPageViewCandidate: sendPageViewCandidate,
    sendSinglePageViewBeacon: sendSinglePageViewBeacon,

    clearImpressionCache: clearImpressionCache,

    getFeedbackTrackingParams: getFeedbackTrackingParams,
    filterUrlParams: filterUrlParams,

    trackAppErrorEvent: function (errorParams) {
        /// <summary>
        ///     This method tracks the app_error event.
        ///     Call getEventTrackingUrl to fire the tracking call
        /// </summary>
        /// <param name="errorParams" type="Object" optional="true">
        ///     errorParams- defined in errorCategory
        /// </param>

        telemetryReadyCallback((): void => {
            trackAppErrorEventInternal(errorParams);
        });
    },

    trackPartialImpression: function (elem, visibilityCheck, mechanism, topLevelContainerType) {
        /// <summary>The track partial impression will fire an impression update for the element graph.</summary>
        /// <param name="$elem">The element graph that contains the data-m attributes</param>
        /// <param name="visibilityCheck">Conduct the visibility checking. By default is false</param>
        /// <param name="mechanism">Mechanism to capture for impression update. By Default is load. Mechanism load is used whenever the module is loaded and rendered to the user.</param>
        /// <param name="topLevelContainerType">TopLevelContainer type for impression update</param>

        telemetryReadyCallback((): void => {
            trackPartialImpressionInternal(elem, visibilityCheck, mechanism, topLevelContainerType);
        });
    },

    trackPage: function (overrideParams?, persistOverriddenParams?) {
        /// <summary>
        ///    Fires page view call for each registered tracking system at the top of <Body /> tag
        /// </summary>
        /// <param name="overrideParams" type="object">override client side params</param>
        /// <param name="persistOverriddenParams" type="boolean">if true overridden params are persisted for other events otherwise false. Default is false</param>

        telemetryReadyCallback((): void => {
            trackPageInternal(overrideParams, persistOverriddenParams);
        });
    },

    trackThirdPartyUpdate: function () {
        /// <summary>
        ///    Fires the third party tracking call for Ajax page refresh scenarios like slide refresh.
        /// </summary>

        mediator.pub("thirdPartyTrackingUpdate");
    },

    register: function (...args: any[]) {
        /// <summary>
        ///   Registers an implementation with the main Tracking function
        /// </summary>
        /// <param name="impl" type="object">
        ///     Implementation of tracking,
        ///     must implement the following functions
        ///     GetEventTrackingUrl(trackInfo)  - generates url for tracking
        /// </param>
        /// <param name="impl" type="object" optional="true" parameterArray="true">
        ///     Implementation of tracking
        /// </param>
        /// <returns type="Object">
        ///     Returns the current object to enable cascading calls
        /// </returns>
        var ndx = 0;
        var trackingImplementation;

        // use double-parens to tell the validator that yes, we INTEND this to be an assignment (=)
        // and not a mistyped equals (==) conditional test.
        while ((trackingImplementation = arguments[ndx++])) {
            if (isNaN(trackingImplementation.samplingRate)) {
                trackingImplementation.samplingRate = 99;
            }
            recipients.push(trackingImplementation);
        }
    },

    getCurrentPageRequestId: function () {
        /// <summary>
        ///    Utility function to return the current page request Id. Empty value will be returned if this method is called before
        ///    telemetry initialization.
        /// </summary>
        /// <returns> the current page request Id
        /// </returns>

        // @ts-ignore
        return pageInstance.getRequestId() || trackInfo.sitePage.requestId || "";
    },

    mapTelemetryParams: function (common, commonMap, params, paramsMap) {
        /// <summary>
        ///    Utility function to gather all the telemetry Params.
        /// </summary>
        /// <param name="common" type="object">
        ///     Object contains common parameters.
        /// </param>
        /// <param name="commonMap" type="object">
        ///     Object contains common param to property mapping
        ///     E.g. map.sitePage:{ a1: "page_name"}
        ///     this will create a querystring a1="value of trackInfo.sitePage.page_name"
        /// </param>
        /// <param name="params" type="object">
        ///     Object contains custom parameters.
        /// </param>
        /// <param name="paramMap" type="object">
        ///     Object contains param to property mapping
        ///     E.g. map.sitePage:{ a1: "page_name"}
        ///     this will create a querystring a1="value of trackInfo.sitePage.page_name"
        /// </param>
        /// <returns type="string">
        ///     url that will be called to do the tracking
        /// </returns>

        var groupKey; 
        var groupValue; 
        var key; 
        var value;
        params = extend({}, common, params);
        // Get params from commonMap
        paramsMap = extend(true, {}, commonMap, paramsMap);
        for (groupKey in paramsMap) {
            var trackInfoGroupKey = trackInfo[groupKey];
            if (trackInfoGroupKey) {
                groupValue = paramsMap[groupKey];
                for (key in groupValue) {
                    // check the override client params first
                    var clientParams = trackInfo.overrideClientParams;
                    if (key && clientParams && clientParams[key] !== undefined) {
                        value = clientParams[key];
                        // if persisting overridden params then override trackInfo group key with value from overridden client params.
                        if (trackInfo.persistOverriddenParams) {
                            trackInfoGroupKey[groupValue[key]] = value;
                        }
                    } else {
                        value = trackInfoGroupKey[groupValue[key]];

                        if (typeof value == "function") {
                            value = value();
                        }
                    }

                    // allow for empty strings but use null or undefined to ignore the parameter entirely
                    if (value != null) {
                        params[key] = value;
                    }
                }
            }
        }

        return params;
    },

    extend: function (obj) {
        extend(true, trackInfo, obj);
    },

    form: function (elem) {
        /// <summary>
        ///    Helper function to bind Form elements that are part of element for tracking
        /// </summary>

        if (!elem || !elem.length) {
            elem = [elem];
        }
        var formElem;
        var ndx = 0;

        // use double-parens to tell the validator that yes, we INTEND this to be an assignment (=)
        // and not a mistyped equals (==) conditional test.
        while ((formElem = elem[ndx++])) {
            // if element is a form element and has not attr "data-customformtrack", bind the submit to it.
            // if the attr "data-customformtrack" is included, it is entirely reponsible for firing its own
            // tracking beacons
            if ("FORM" == domName(formElem) && !attr(formElem, "data-customformtrack")) {
                events.bind(formElem, "submit", trackFormSubmitEvent);
            }
        }
    },

    preventBeacon: function (beaconId) {
        /// <summary>Set the value isGeneratedEarly to prevent the sending of a beacon.</summary>
        /// <param name="beaconId">The name of the beacon as listed in the markets.config file.</param>
        if (beaconId) {
            beaconId = beaconId.toLowerCase();

            // loop through each implementation
            for (var ndx = 0; ndx < recipients.length; ndx++) {
                var defOpts = recipients[ndx].defaultOpts;
                if (beaconId === defOpts.id.toLowerCase()) {
                    defOpts.isGeneratedEarly = true;
                    break;
                }
            }
        }
    },

    trackPageRefresh: function (overrideParams?, persistOverriddenParams?) {
        /// <summary>
        ///    Fires page view call for each registered tracking system at the top of <Body /> tag and resets activity Id.
        /// </summary>
        /// <param name="overrideParams" type="object">override client side params</param>
        /// <param name="persistOverriddenParams" type="boolean">if true overridden params are persisted for other events otherwise false. Default is false</param>

        // calling pageInstance.updatePageInstance() instead of using mediator
        // to eliminate delay rid update when executed via mediator.pub
        pageInstance.updatePageInstance();
        clearImpressionCache();
        trackPageInternal(overrideParams, persistOverriddenParams);

        setTimeout(
            () => {
                trackPartialImpressionInternal(document.getElementById("root"), true, "load", null);
            },
            1000);
    }
};

// find's all the children dom elements that have the 'data-m' attribute defined.
// also returns the current element if it meets the criteria.
function findTelemetryElements(domElement) {
    var elements = [];

    // for modern browsers I can use querySelectorAll to find the child elements.
    if (domElement && domElement.querySelectorAll) {
        var telemetryChildren = domElement.querySelectorAll('[data-m]');

        // include the root if applicable
        if (attr(domElement, 'data-m')) {
            elements.push(domElement);
        }

        // do the children
        for (var index = 0; index < telemetryChildren.length; index++) {
            elements.push(telemetryChildren[index]);
        }
    }

    // return the elements
    return elements;
}

// a helper function that will parse json contained within attributes
function parseDomAttributeJson(element, attributeName) {
    var dataAttr = attr(element, attributeName);
    if (dataAttr) {
        try {
            return JSON.parse(dataAttr);
        } catch (e) {
            return null;
        }
    }

    return null;
}

// a helper function to check if an array contains an element.
function contains(item, array) {
    if (array.indexOf) {
        return array.indexOf(item) !== -1;
    } else {
        // for IE8
        for (var i = 0; i < array.length; i++) {
            if (array[i] === item) {
                return true;
            }
        }
    }

    return false;
}

function trackPartialImpressionInternal(elem, visibilityCheck, mechanism, topLevelContainerType) {
    /// <summary>The track partial impression will fire an OTF impression update for the element graph.</summary>
    /// <param name="$elem">The element graph that contains the data-m attributes</param>
    /// <param name="visibilityCheck">Conduct the visibility checking. By default is false</param>
    /// <param name="mechanism">Mechanism to capture for impression update. By Default is load</param>
    /// <param name="topLevelContainerType">TopLevelContainer type for impression update. By Default is load</param>

    var perfMarkerTrackingPartialImprUpdateStart = "TimeToTrackingPartialImprUpdateStart";
    var perfMarkerTrackingPartialImprUpdateEnd = "TimeToTrackingPartialImprUpdateEnd";

    if (elem) {
        if (elem.jquery) {
            elem = elem[0];
        }

        // retrieve the telemetry elements.
        var elements = findTelemetryElements(elem);
        var formattedModules = [];

        for (var index = 0; index < elements.length; index++) {
            var element = elements[index];
            if (visibilityCheck && !trackInfo.telemetryTracking.isVisible(element)) {
                continue;
            }

            var impressionContent = parseDomAttributeJson(element, 'data-m');
            var id = impressionContent ? impressionContent.i : undefined;

            // ensure we haven't already sent the payload
            if (id !== undefined && !contains(id, impressionCache)) {
                // add it to our cache
                impressionCache.push(id);
                formattedModules.push(impressionContent);

                // TODO: (nubenja) Remove this once data-m.y is deprecated
                // Convertion from telemetryType (data-m.y) to telemetryContentType and telemetryContentAction (data-m.ct/ca)
                if (impressionContent.y && !impressionContent.ct) {
                    var ct = telemetryDataTypes.getTelemetryContentType(impressionContent.y);

                    if (ct) {
                        impressionContent.ct = ct;
                    }
                }
            }
        }

        var context = {};
        if (formattedModules && formattedModules.length > 0) {
            // @ts-ignore
            context.e = formattedModules;
        }

        // fire the telemetry event if we were able to get any impression data.
        // @ts-ignore
        if (context && context.e && context.e.length > 0) {
            var mech = (mechanism) ? mechanism : trackInfo.imprUpdateMechanisms.partial;
            trackInfo.telemetryTracking.sendImprUpdateEvent(mech, context, topLevelContainerType);
        }
    }
}

function clearImpressionCache() {
    impressionCache = [];
}

function getEventFromUrl(targetUrl) {
    /// <summary>
    /// Check the Current Url and get the event name from the targetUrl.
    /// The URL format for the specifying the event name is - url#tevt=EVENTNAME;OTHER_NAME_VALUE_PAIRS
    /// </summary>
    /// <param name="targetUrl" type="string">
    /// The target Url for the event.
    /// </param>
    /// <returns type="string"> event name, if it is specified, else null. </returns>

    /*
    For input  value : "http://www.bing.com/videos/search?q=&mkt=en-GB#tevt=click_nonnav;"
    The result will be like this-
    [#tevt=click_nonnav;,click_nonnav,;]
    [0] : "#tevt=click_nonnav;",
    [1] : "click_nonnav",
    [2] : ";",

    The  1st element contains the event name.
    */

    // If event name is not found, return null, the original event name will be kept.
    var result = new RegExp(regExpEventName).exec(targetUrl);
    return (result && result.length && result[1]) || null;
};

function getClickEventFromUrl(targetUrl) {
    /// <summary>
    /// Check the Current Url -
    /// Case 1-  if the Url format is - baseurl/#, then treat it as nonNav event.
    ///     Match Domain name.
    ///     If the Url of current page and target page is same and only # is at the end, then it is not navigating away from the page.
    ///
    /// Case 2 - the Url format is url#tevt=EVENTNAME;OTHER_NAME_VALUE_PAIRS,
    ///            extract the event name from the Url. the event name is terminated with ;
    /// </summary>
    /// <param name="targetUrl" type="string">
    /// The target Url for the event.
    /// </param>
    /// <returns type="string">
    /// event name; if it is a non nav url and no event name is specified then return the default non nav click event name,
    /// otherwise, return the event name specified in the url.
    /// </returns>

    var evtName = null;
    if (targetUrl) {
        // if the target url contains tevt=, then this url contains the event name. We will extract the event name from url.
        if (targetUrl.indexOf(tEvtTag) == -1) {
            //  If the Url format is - http://www.msn.com/#, then categorize it as nonnav click.
            // Convert the baseurl and currentUrl in lowercase for comparison.
            var baseUrl = targetUrl.substring(0, targetUrl.indexOf('#')).toLowerCase();
            var curUrl = getLocationHref().toLowerCase();
            if (curUrl == baseUrl || curUrl.substring(0, curUrl.indexOf('#')) == baseUrl) {
                evtName = trackInfo.eventTypes.nonNavClckEvt;
            }
        } else {
            // extract the event name from url.
            evtName = getEventFromUrl(targetUrl);
        }
    }
    // Return  null, so that the original event is kept.
    return evtName;
};

function createEvt(event, eventName, targetElem) {
    /// <summary>
    /// Creates an event based upon the properties of the base event.
    /// </summary>
    /// <param name="event" type="object">
    /// This is a an input event parameter. Some basic properties are copied from this param to the the event created.
    /// </param>
    /// <param name="eventName" type="string">
    /// Name of the event to be created.
    /// </param>
    /// <param name="targetElem" type="object">
    /// Target element that need to be attached to the event being created in this method.
    /// </param>
    /// <returns type="object">An object is created with base properties from the event passed as param. This object is then returned. </returns>

    var evt = null;

    if (doc && doc.createEvent) {  // Supported by non-IE based browsers.
        evt = doc.createEvent("Events");
        evt.initEvent(eventName, false, true, targetElem || window,
            0, 0, 0, 0, 0, false, false, false, false, 0, null);
        // @ts-ignore
    } else if (doc && doc.createEventObject) {  // Supported by IE based browsers.
        // @ts-ignore
        evt = doc.createEventObject(event);
        evt.type = eventName;
    }

    if (evt) {
        evt.customTarget = targetElem;

        // Store the original event type in a field
        evt.originalEvent = event && event.type;
    }

    return evt;
};

function preventEventDefault(event) {
    /// <summary>
    /// Cancels the event and prevents from being passed further.
    /// </summary>
    /// <param name="event" type="object">
    /// Input event parameter that need to be canceled.
    /// </param>
    if (event.preventDefault) {   // For Non IE
        event.preventDefault();
    } else {    // For IE
        event.returnValue = false;
    }
};

function onContextMenu(event) {
    /// <summary>
    ///     onContextMenu Handler
    ///     we'll only track context menu events if the target is a link (A or AREA)
    ///     This event is used to keep track of when the context menu opens....
    ///     Right click does not generate onClick events, so we are tracking context menu on all the form factors.
    /// </summary>
    trackClickEvents(event);
};

function onClick(event) {
    /// <summary>
    ///     OnClick Handler
    ///     we'll only track clicks if the target is a link (A or AREA)
    ///     AND the mouse button isn't the RIGHT button.
    ///     MOST browsers think the mouse button value is 2. For those that don't, this
    ///     might have to be revisited.
    /// </summary>
    if (2 == event.button) {
        return;
    }

    trackClickEvents(event);
};

/// <summary>
/// Computes the lineage of a particular element, passing back the object containing array of 'n's and array of 'o's on the dom tree.
/// </summary>
/// <returns>An object containing the array of 'n's and array of 'o's on the dom tree starting with the current element, or an empty array if none are found.</returns>
function getLineage(element) {
    var result: any = {};
    var nameLineageArray = [];
    var ordinalLineageArray = [];

    while (element) {
        var telemetryObject = parseDomAttributeJson(element, 'data-m');
        if (telemetryObject) {
            if (telemetryObject.n) {
                nameLineageArray.push(telemetryObject.n);
            }

            if (telemetryObject.o) {
                ordinalLineageArray.push(telemetryObject.o);
            }
        }
        element = getParent(element);
    }

    result.nameLineage = nameLineageArray;
    result.ordinalLineage = ordinalLineageArray;
    return result;
}

function isClickedTwice(currentTarget) {
    /// <summary>
    ///     isClickedTwice method
    ///     Identify if more than one click event happened on the same element in short span of time.
    /// </summary>
    /// <returns>Return true if the user click on the same element twice in short span of time, otherwise false.</returns>

    // if lastClickedTarget is same as currentTarget means user clicked on same elements twice to be considered as double click.
    try {
        if (currentTarget && lastClickedTarget && lastClickedTarget === currentTarget) {
            return true;
        }
        lastClickedTarget = currentTarget;
        setTimeout(function () {
            // Reset 'lastClickedTarget' after a ceratin time period to avoid any kind of unexpected situation.
            lastClickedTarget = null;
        }, clickTargetResetTimeoutInMs);
    } catch (ex) {
        lastClickedTarget = null;
        return false;
    }
    return false;
}

function trackClickEvents(event) {
    /// <summary>
    ///     trackClickEvents method
    ///     Tracks all the click events on links.
    /// </summary>

    // handle error scenarios.
    if (!event) {
        return;
    }

    // If the event or in the originalEvent the developer has set customHandled=true, which means its been already explicitly fired.
    // In those cases, don't fire the implicit event.
    if (event.customHandled || (event.originalEvent && event.originalEvent.customHandled)) {
        return;
    }

    // Temp. Hack for Image Map: In IE9, srcElement associated with window.event is not returning correct element.
    // Instead of returning a <Area> element, it is returning <IMG> element. <IMG> element has no href attribute, neither it's parents do have.
    // For this reason, Tracking calls are not fired in IE9.
    // This hack will check if the event belongs to ImageMap. If so, it will pick activeElement from document and assign it as target/customTarget.
    try {
        // Passing the time parameter inside event object with timestamp as a value.
        event.time = trackInfo.getCurrentTime();
        if (event.customTarget && event.customTarget.useMap) {
            event.customTarget = event.customTarget.document.activeElement;
            event.target = event.customTarget;
        }
    } catch (ex) { }

    // we assume that an element with an href (and <a> or an <area>) is the click-tracking element.
    // we also want to target <button> events -- they get clicked, too, but have no href, yet we
    // still want to track them as well. Now that we have added additional actions in contextmenu we want to
    // track click event on MENUITEM element as well.
    var target = dom.getTarget(event);
    while (target && !attr(target, "href") && target.nodeName != "BUTTON" && target.nodeName != "MENUITEM") {
        target = getParent(target);
    }

    // // if the target element is part of the service waffle experience, we want to stop the automatic click tracking
    if (target && (window as any).ServiceWaffleAutomaticClickTrackingDisabled) {
        let appLauncherElement = document.getElementById('appLauncher');
        if (target.className.includes("o365sx-button") || (appLauncherElement && appLauncherElement.contains(target))) {
            return;
        }
    }

    // if the target element is specified custom handled, we don't want track any event
    if (target && target.hasAttribute && target.hasAttribute("data-customhandled")) {
        return;
    }

    // If the user clicks to an element twice like a double click, it will return.
    if (target && isClickedTwice(target)) {
        return;
    }

    if (event.customTags) {
        var customTags = event.customTags;
        if (typeof customTags === "object") {
            for (var key in customTags) {
                if (key) {
                    var val = customTags[key];
                    if (val && val != "undefined") {
                        trackInfo.setCustomTagsForClickEvent(key, val);
                    }
                }
            }
        }
    }

    // if the target element is a button which is part of a form we don't want a non-nav track event fired since form elements are tracked separately as submit events
    // check to see if the button is part of a form otherwsise two beacons will  fire inadvertently , one as a non-nav event and one as a submit event
    if (target && !target.form) {
        var evt = event;

        if (event.type == trackInfo.eventTypes.contextmenu) {
            evt = createEvt(event, trackInfo.eventTypes.click, target);
        }

        // if href attr is not defined, then target will be null.
        var tHref = target["href"];
        if (tHref && tHref.length) {
            // if the url ending with #, check if it is non Nav Url.
            if (tHref.indexOf('#') == tHref.length - 1 || tHref.indexOf(tEvtTag) >= 0) {
                // Create new event only for click events.
                // Don't do any action for contextmenu events.
                if (event.type == trackInfo.eventTypes.click) {
                    var evtName = getClickEventFromUrl(tHref);
                    if (evtName) {
                        evt = createEvt(event, trackInfo.eventTypes.nonNavClckEvt, target);
                    }
                } else if (event.type == trackInfo.eventTypes.contextmenu) {
                    return;
                }
            }

            // prevent the default action for non click events, so that it does not show up on the address bar.
            if (evt.type == trackInfo.eventTypes.nonNavClckEvt) {
                preventEventDefault(event);
            }

            // Making sure that event.contentAction is part of final evt payload since it is not part of event object creation.
            if (event.contentAction) {
                evt.contentAction = event.contentAction;
            }

            trackingHelper.addOcidToHref(tHref, target, evt);
            if (!evt.isPropagationStopped) {
                trackEvent(evt, target);
            }
        } else {
            //overriding the click from non_navclick to click if telemetry-override attribute is present on the page
            var overrideClickTelemetry = event.target.getAttribute("telemetry-override");
            if (!overrideClickTelemetry) {
                //check for imminient parent
                overrideClickTelemetry = getParent(event.target) && getParent(event.target).getAttribute("telemetry-override");
                if (!overrideClickTelemetry) {
                    //check for imminient parent
                    overrideClickTelemetry = getParent(event.target) && getParent(getParent(event.target)) && getParent(getParent(event.target)).getAttribute("telemetry-override");
                }
            }
            // no href -- probably a button; which would be a non-nav event.
            // don't track this if evt type is contextmenu.
            if (event.type != trackInfo.eventTypes.contextmenu) {
                if (event.contentAction) {
                    trackEvent({ type: trackInfo.eventTypes.nonNavClckEvt, target: target, contentAction: event.contentAction });
                } else if (overrideClickTelemetry === "click") {
                    trackEvent({ type: trackInfo.eventTypes.click, target: target });
                } else {
                    trackEvent({ type: trackInfo.eventTypes.nonNavClckEvt, target: target });
                }
            }
        }
    }
}

function trackVideoEvent(params, isInline) {
    /// <summary>send video event using new pub-sub model</summary>
    /// <param name="params" type="string">telemetry params</param>
    /// <param name="isInline" type="string">is video using inlined player</param>
    if (!params || !params["evt"] || !trackInfo.shouldFireTelemetry()) {
        return;
    }

    let actionType;
    let behaviorType;

    if (params["evt"] === trackInfo.eventTypes.videoClickNonNav) {
        actionType = telemetryDataTypes.TelemetryActionType.Click;
        behaviorType = telemetryDataTypes.TelemetryBehaviorType.Undefined;
    }

    // There is a case they send as integer
    if (params["adid"]) {
        params["adid"] = params["adid"].toString();
    }

    var sitePage = trackInfo.sitePage;
    var client = trackInfo.client;
    var userDynamic = trackInfo.userDynamic;
    var setupParameters = trackInfo.telemetryTracking.setupParameters(params["evt"]);

    var defaultParams = {
        "at": actionType,
        "bt": behaviorType,
        "bh": client.height(),
        "bw": client.width(),
        // @ts-ignore
        "cvs": sitePage.canvas,
        // @ts-ignore
        "mkt": sitePage.localeCode,
        // @ts-ignore
        "pg.t": sitePage.page_type,
        // @ts-ignore
        "pg.n": sitePage.page_name,
        // @ts-ignore
        "rid": sitePage.serverImpressionGuid,
        // Mimic the same current behavior in VideoPlayer code dpt and sdpt
        // @ts-ignore
        "st.dpt": sitePage.department || params["st.dpt"] || "watch",
        // @ts-ignore
        "st.sdpt": sitePage.subDepartment || params["st.sdpt"] || "no-sc",
        // @ts-ignore
        "subcvs": sitePage.vertical,
        // @ts-ignore
        "flightid": sitePage.flightid,
        // Add extra bool param for Omniture scenario
        // where we would like to distinguish between video and non video events (JSLL)
        "isVideo": true,
        "idx": isInline ? userDynamic.firstEventIndex() : userDynamic.currentEventIndex()
    };

    var videoPackage = extend({}, params, setupParameters, defaultParams);

    var cleanParam = removeNullOrEmptyParams(videoPackage);

    var beaconData = {
        // @ts-ignore
        url: sitePage.otfURL,
        requestType: telemetryConstants.GET,
        eventType: cleanParam["evt"],
        params: cleanParam,
        trackingType: telemetryConstants.OTF,
        eventSource: "VideoPlayer"
    };

    mediator.pub(telemetryConstants.FireTelemetry, beaconData);
}

function trackFeedbackEvent(params) {
    /// <summary>send feedback event data</summary>
    /// <param name="params" type="string">telemetry params</param>
    if (!params || !trackInfo.shouldFireTelemetry()) {
        return;
    }

    // Map only valid params to final json object
    var feedbackParams =
    {
        "feedback.id": params["feedback.id"],
        "feedback.source": params["feedback.source"],
        "feedback.area": params["feedback.area"],
        "feedback.type": params["feedback.type"],
        "feedback.rating": params["feedback.rating"],
        submitValue: params["submitValue"],
        userAgent: params["userAgent"]
    };

    var otfPackage = extend(true, {}, feedbackParams, getFeedbackTrackingParams("feedback"));

    // Clean up all undefined values
    otfPackage = removeNullOrEmptyParams(otfPackage);

    // Fire an event via sendUpdate (POST)
    trackInfo.telemetryTracking.sendUpdate(otfPackage);
}

function getFeedbackTrackingParams(evt) {
    /// <summary>Get feedback tracking tracking properties</summary>
    /// <param name="params" type="object">parameters to send</param>
    var setupParameters = trackInfo.telemetryTracking.setupParameters(evt);
    var sitePage = trackInfo.sitePage;

    var params =
    {
        scr: trackInfo.client ? trackInfo.client.screenResolution() : "",
        // @ts-ignore
        "client.deviceModel": sitePage.d_dgk,
        // @ts-ignore
        pageType: sitePage.page_name,
        // @ts-ignore
        subcvs: sitePage.vertical,
        // @ts-ignore
        cvs: sitePage.canvas,
        // @ts-ignore
        contentid: sitePage.entityId,
        // @ts-ignore
        cu: trackInfo.client ? encodeURIComponent(trackInfo.client.pageUrl()) : sitePage.pageUrl,
        // @ts-ignore
        mkt: sitePage.localeCode,
        // @ts-ignore
        flightid: sitePage.flightid,
        // @ts-ignore
        signedin: trackInfo.userStatic ? trackInfo.userStatic.isSignedIn : ""
    };

    // override or extend the params from setup parameters
    if (setupParameters !== null && setupParameters !== undefined) {
        params = extend(true, {}, params, setupParameters);
    }

    return params;
}

function removeNullOrEmptyParams(params) {
    /// <summary>remove undefined from params object</summary>
    /// <param name="params" type="string">telemetry params</param>
    var cleanParam = {};
    for (var param in params) {
        if (params[param] != undefined) {
            cleanParam[param] = params[param];
        }
    }

    return cleanParam;
}

function filterUrlParams(url, blackList) {
    /// <summary>Filter out all query params in given url from blackList array</summary>
    /// <param name="url" type="string">url string</param>
    /// <param name="blackList" type="array">query params that will be deleted.</param>
    if (url && blackList) {
        var splitedUrl = url.split("?");
        if (splitedUrl.length > 1) {
            var outputUrl = splitedUrl[0] + "?";
            var paramsString = splitedUrl[1];
            var params = navigation.getParams(paramsString);
            if (paramsString) {
                for (var i = 0; i < blackList.length; i++) {
                    blackList[i] = blackList[i].toLowerCase();
                }
                for (var param in params) {
                    if (!contains(param.toLowerCase(), blackList)) {
                        outputUrl = outputUrl + param + "=" + params[param] + "&";
                    }
                }
            }
            return outputUrl.slice(0, -1);
        }
    }
    return url;
}

function logDiag(msg, isError) {
    /// <summary>Debug tracing method. </summary>
    /// <param name="msg" type="string">Log message</param>
    /// <param name="isError" type="boolean">Flag to indicate whether to log the message as an error.</param>

    if (isError) {
        logger.logError(msg);
    } else {
        logger.log(msg);
    }
}
